import React from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loadUser, recordPayment } from '../../containers/UserPage/reducer';
import { selectUser } from '../../containers/UserPage/selectors';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { AdminLoading, AdminNotice, AdminTable } from '../components/V2AdminUI';
import './users.css';

const V2UserDetail: React.FC = () => {
  useIsAdmin();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const userId = Number(id);
  const validId = Number.isSafeInteger(userId) && userId > 0;
  const loadedUser = useSelector(selectUser);
  const user = loadedUser?.user.id === userId ? loadedUser : undefined;
  const [amount, setAmount] = React.useState('');
  const [commissionPercent, setCommissionPercent] = React.useState('10');
  const [referrerEmail, setReferrerEmail] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (validId) dispatch(loadUser(userId));
    setAmount('');
    setNote('');
    setCommissionPercent('10');
  }, [dispatch, userId, validId]);

  React.useEffect(() => {
    setReferrerEmail(user?.user.referrerEmail ?? '');
  }, [userId, user?.user.referrerEmail]);

  const submitPayment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedAmount = Number(amount.trim().replace(',', '.'));
    const parsedPercent = Number(commissionPercent.trim().replace(',', '.'));
    if (!validId || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error(t('admin_user_invalid_amount'));
      return;
    }
    if (
      !commissionPercent.trim() ||
      !Number.isFinite(parsedPercent) ||
      parsedPercent < 0 ||
      parsedPercent > 100
    ) {
      toast.error(t('admin_user_invalid_commission'));
      return;
    }
    dispatch(
      recordPayment({
        userId,
        amount: parsedAmount,
        commissionPercent: parsedPercent,
        referrerEmail: referrerEmail.trim() || undefined,
        note: note.trim() || undefined,
      }),
    );
    setAmount('');
    setNote('');
  };

  if (!validId) return <AdminNotice type="error">{t('admin_user_invalid_id')}</AdminNotice>;
  if (!user) {
    return (
      <section className="settings-panel">
        <AdminLoading />
        <button type="button" className="outline-button" onClick={() => dispatch(loadUser(userId))}>
          {t('retry')}
        </button>
      </section>
    );
  }

  return (
    <div className="v2-admin-user-detail">
      <section className="settings-panel v2-admin-user-card">
        <div className="settings-panel__head">
          <h2>{user.user.email}</h2>
        </div>
        {user.user.referrerEmail ? (
          <p className="v2-admin-user-meta">
            {t('admin_user_referrer')}: <strong>{user.user.referrerEmail}</strong>
          </p>
        ) : (
          <p className="v2-admin-user-meta muted">{t('admin_user_no_referrer')}</p>
        )}
      </section>

      <section className="settings-panel v2-admin-user-card">
        <div className="settings-panel__head">
          <h2>{t('admin_user_record_payment')}</h2>
        </div>
        <p className="settings-field-hint">{t('admin_user_commission_hint')}</p>
        <form className="v2-admin-native-payment-form" onSubmit={submitPayment}>
          <div className="v2-admin-payment-grid">
            <label className="settings-field">
              <span className="settings-field-label">{t('admin_user_amount')}</span>
              <input
                className="v2-admin-input"
                type="text"
                inputMode="decimal"
                required
                placeholder="100"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">{t('admin_user_commission')}</span>
              <input
                className="v2-admin-input"
                type="text"
                inputMode="decimal"
                required
                placeholder="10"
                value={commissionPercent}
                onChange={(event) => setCommissionPercent(event.target.value)}
              />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">{t('admin_user_referrer_email')}</span>
              <input
                className="v2-admin-input"
                type="email"
                autoComplete="off"
                placeholder="referrer@example.com"
                value={referrerEmail}
                onChange={(event) => setReferrerEmail(event.target.value)}
              />
            </label>
            <label className="settings-field v2-admin-payment-grid__wide">
              <span className="settings-field-label">{t('admin_user_note')}</span>
              <input
                className="v2-admin-input"
                placeholder={t('admin_user_optional')}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </div>
          <div className="settings-panel__footer">
            <button type="submit" className="acid-button">
              {t('admin_user_record_payment')}
            </button>
          </div>
        </form>
      </section>

      <section className="settings-panel v2-admin-user-card">
        <div className="settings-panel__head">
          <h2>{t('admin_user_search_activity')}</h2>
        </div>
        <AdminTable
          columns={[
            {
              key: 'date',
              title: t('admin_user_date'),
              render: (row) => dayjs(row.date).format('YYYY-MM-DD'),
            },
            { key: 'searches', title: t('admin_user_searches'), render: (row) => row.searches },
          ]}
          rows={user.statistics ?? []}
          rowKey="id"
          emptyText={t('admin_user_searches_empty')}
          minWidth={260}
          caption={t('admin_user_search_activity')}
        />
      </section>
    </div>
  );
};

export default V2UserDetail;
